'use client';

import { useEffect, useRef } from 'react';

/**
 * Calls a function on each animation frame, with the delta time since the last frame.
 */
export const useAnimationFrameLoop = (
    callback: (delta: number) => void,
): {
    start: () => void;
    stop: () => void;
} => {
    const requestRef = useRef<number>();
    const lastTimeRef = useRef<number>(performance.now());
    const callbackRef = useRef<(delta: number) => void>(callback);
    const animationRunningRef = useRef<boolean>(false);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    const animate = (time: number) => {
        if (!animationRunningRef.current) return;

        const delta = (time - lastTimeRef.current) / 1000;
        lastTimeRef.current = time;

        // The first animation frame can have no time delta, which would jump straight to the end state.
        if (delta > 0) {
            callbackRef.current?.(delta);
        }

        requestRef.current = requestAnimationFrame(animate);
    };

    const start = () => {
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
        animationRunningRef.current = true;

        lastTimeRef.current = performance.now();
        requestRef.current = requestAnimationFrame(animate);
    };

    const stop = () => {
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
        animationRunningRef.current = false;
    };

    return { start, stop };
};
